import React, { useEffect } from 'react'

import AssemblingOrdersPage from '../../../containers/order/AssemblingOrdersPage'
import AssemblersManagementPage from '../../../containers/assembling/AssemblersManagementPage'
import AssemblingSettingsPage from '../../../containers/assembling/settings/AssemblingSettingsPage'
import AssemblingShiftsPlanner from '../../../containers/assembling/shifts/AssemblingShiftsPlanner'
import CommonErrorDisplay from '@components/common/CommonErrorDisplay'

import { 
  ROUTE_ASSEMBLING_MANAGEMENT
} from '@@partner:config/routes'

import CommonSubTopMenu from '@components/common/subTopMenu/CommonSubTopMenu'
import CommonTopMenu, { TopMenuElement } from '@components/common/CommonTopMenu'

import ListIcon from '@rsuite/icons/List'
import OperatePeopleIcon from '@rsuite/icons/OperatePeople'
import SettingHorizontalIcon from '@rsuite/icons/SettingHorizontal'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import NoticeIcon from '@rsuite/icons/Notice'
import BarChartIcon from '@rsuite/icons/BarChart'
import CalendarIcon from '@rsuite/icons/Calendar'
import { generatePath } from 'react-router-dom'
import { usePartner } from '@@partner:helpers/hooks/auth'

export type AssemblingStructure = 'orders' | 'assemblers' | 'settings' | 'reasons' | 'notifications' | 'alerts' | 'shifts'

type ComponentConfig = {
  icon?: React.ReactNode
  title: string
  type: AssemblingStructure
  hideOnTopMenu?: boolean
  component: React.FC<any>
  subPages?: {
    icon: React.ReactNode
    title: string
    to: string
  }[]
}

const componentsMap: Record<string, ComponentConfig> = {
  orders: {
    icon: <ListIcon />,
    type: 'orders',
    title: 'Заказы',
    component: AssemblingOrdersPage
  },
  assemblers: {
    icon: <OperatePeopleIcon />,
    type: 'assemblers',
    title: 'Сборщики',
    component: AssemblersManagementPage
  },
  shifts: {
    icon: <CalendarIcon />,
    type: 'shifts',
    title: 'Смены',
    component: AssemblingShiftsPlanner
  },
  settings: {
    icon: <SettingHorizontalIcon />,
    type: 'settings',
    title: 'Настройки',
    component: AssemblingSettingsPage
  }
}

export interface AssemblingManagementUnitProps {
  type: string
  onChangePage?: (title: string) => void
}

export default function AssemblingManagementUnit ({
  type = 'orders',
  onChangePage
}: AssemblingManagementUnitProps) {
  const { brandSlug, isBrandLoaded } = usePartner()

  const { component: Component = null, title, subPages } = componentsMap?.[type] ?? {}

  useEffect(() => {
    if (title && onChangePage) {
      onChangePage(title)
    }
    else {
      onChangePage?.('Сборка')
    }
  }, [ title, onChangePage ])

  // Guard: ждём загрузки бренда для корректной генерации URL
  if (!isBrandLoaded) {
    return null
  }

  const topMenuElements: TopMenuElement[] = Object.entries(componentsMap)
    .filter(([ key, { hideOnTopMenu } ]) => !hideOnTopMenu)
    .map(([ key, { icon, title } ]) => ({
      icon,
      to: generatePath(ROUTE_ASSEMBLING_MANAGEMENT, {
        brandSlug: brandSlug!,
        type: key
      }),
      title
    }))

  if (!Component) {
    return (
      <CommonErrorDisplay
        title="Ошибка загрузки компонента"
        error={
          new Error(`Компонент для отображения запроса ${type} не найден!`)
        }
      />
    )
  }

  return (
    <div>
      <CommonTopMenu elements={topMenuElements} />
      { subPages && subPages.length > 0 ? (
        <CommonSubTopMenu
          elements={subPages.map(item => ({
            ...item,
            to: generatePath(item.to, { brandSlug })
          }))}
        />
      ) : null }
      <Component />
    </div>
  )
}
